import axios from "axios";

// Strip trailing slashes: "https://host/" + "/api" would become "//api" and miss the API router.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 120000,
  withCredentials: true,
});

export const createSession = async (company) => {
  const { data } = await api.post("/sessions", company ? { company } : {});
  return data;
};

export const getSession = async (sid) => {
  const { data } = await api.get(`/sessions/${sid}`);
  return data;
};

export const sendMessage = async (sid, text) => {
  const { data } = await api.post(`/sessions/${sid}/message`, { text });
  return data;
};

export const finalizeSession = async (sid) => {
  const { data } = await api.post(`/sessions/${sid}/finalize`);
  return data;
};

export const getReport = async (sid) => {
  const { data } = await api.get(`/sessions/${sid}/report`);
  return data;
};

export const excelUrl = (sid) => `${API_BASE}/sessions/${sid}/export/excel`;
export const chartUrl = (sid) => `${API_BASE}/sessions/${sid}/export/chart.png`;
export const chartPdfUrl = (sid) => `${API_BASE}/sessions/${sid}/export/chart.pdf`;

// ---- Dashboard & Agent config exports ----
export const dashboardExcelUrl = (window = 1) => `${API_BASE}/dashboard/export/excel?window=${window}`;
export const dashboardPdfUrl = (window = 1) => `${API_BASE}/dashboard/export/pdf?window=${window}`;
export const agentJsonUrl = (slug) => `${API_BASE}/agents/${slug}/export/json`;
export const agentMarkdownUrl = (slug) => `${API_BASE}/agents/${slug}/export/markdown`;
export const agentPdfUrl = (slug) => `${API_BASE}/agents/${slug}/export/pdf`;

// ---- Webhooks & events ----
export const getWebhookInfo = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/webhook/info`);
  return data;
};

export const rotateWebhookSecret = async (slug) => {
  const { data } = await api.post(`/agents/${slug}/webhook/rotate`);
  return data;
};

export const listAgentEvents = async (slug, limit = 100) => {
  const { data } = await api.get(`/agents/${slug}/events`, { params: { limit } });
  return data;
};

// ---- Admin · Billing Plans ----
export const adminListPlans = async () => {
  const { data } = await api.get(`/admin/plans`);
  return data;
};
export const adminUnits = async () => {
  const { data } = await api.get(`/admin/plans/units`);
  return data;
};
export const adminCreatePlan = async (payload) => {
  const { data } = await api.post(`/admin/plans`, payload);
  return data;
};
export const adminUpdatePlan = async (id, payload) => {
  const { data } = await api.put(`/admin/plans/${id}`, payload);
  return data;
};
export const adminDeletePlan = async (id) => {
  const { data } = await api.delete(`/admin/plans/${id}`);
  return data;
};
export const adminAssignPlan = async (slug, planId) => {
  const { data } = await api.post(`/admin/agents/${slug}/plan`, { plan_id: planId || null });
  return data;
};
export const adminBilling = async (window = 1) => {
  const { data } = await api.get(`/admin/billing`, { params: { window } });
  return data;
};

// ---- KBL ----
export const kblSupported = async () => {
  const { data } = await api.get(`/kbl/supported`);
  return data;
};
export const kblList = async (company, department) => {
  const params = {};
  if (company) params.company = company;
  if (department) params.department = department;
  const { data } = await api.get(`/kbl`, { params });
  return data;
};
export const kblCompanies = async () => {
  const { data } = await api.get(`/kbl/companies`);
  return data;
};
export const kblUpload = async ({ file, company, department, tags }) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("company", company);
  fd.append("department", department);
  if (tags) fd.append("tags", tags);
  const { data } = await api.post(`/kbl/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};
export const kblDelete = async (id) => {
  const { data } = await api.delete(`/kbl/${id}`);
  return data;
};
export const kblSearch = async ({ company, department, query, top_k = 5 }) => {
  const { data } = await api.post(`/kbl/search`, { company, department, query, top_k });
  return data;
};
export const kblReindex = async (company) => {
  const params = company ? { company } : {};
  const { data } = await api.post(`/kbl/reindex`, null, { params });
  return data;
};
export const kblStats = async (company) => {
  const params = company ? { company } : {};
  const { data } = await api.get(`/kbl/stats`, { params });
  return data;
};

// ----- Industry library (industry × department hints) -----
export const industryLibList = async (industry) => {
  const params = industry ? { industry } : {};
  const { data } = await api.get(`/admin/industry-library`, { params });
  return data;
};
export const industryLibUpsert = async (body) => {
  const { data } = await api.put(`/admin/industry-library`, body);
  return data;
};
export const industryLibDelete = async (hintId) => {
  const { data } = await api.delete(`/admin/industry-library/${hintId}`);
  return data;
};
export const industryCuratorDepartments = async () => {
  const { data } = await api.get(`/admin/industry-library/curator/departments`);
  return data;
};
export const industryCurate = async (body) => {
  // Enqueues a background curator job and returns {job, status:"queued"}
  const { data } = await api.post(`/admin/industry-library/curate`, body);
  return data;
};
export const industryCurateJobStatus = async (jobId) => {
  const { data } = await api.get(`/admin/industry-library/curate/jobs/${jobId}`);
  return data;
};
export const industryBulkSave = async (body) => {
  const { data } = await api.post(`/admin/industry-library/bulk-save`, body);
  return data;
};

// ----- MCP catalog & per-agent MCP requirements -----
export const mcpCatalog = async () => {
  const { data } = await api.get(`/mcp/catalog`);
  return data;
};
export const agentMcpRequired = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/mcp/required`);
  return data;
};
export const agentMcpList = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/mcp`);
  return data;
};
export const agentMcpUpsert = async (slug, tool, config) => {
  const { data } = await api.put(`/agents/${slug}/mcp/${tool}`, { config });
  return data;
};
export const agentMcpDelete = async (slug, tool) => {
  const { data } = await api.delete(`/agents/${slug}/mcp/${tool}`);
  return data;
};
export const adminMcpCustomCreate = async (body) => {
  const { data } = await api.post(`/admin/mcp/custom`, body);
  return data;
};
export const adminMcpCustomDelete = async (id) => {
  const { data } = await api.delete(`/admin/mcp/custom/${id}`);
  return data;
};

// ----- Admin · Demo data + Companies -----
export const adminDemoStatus = async () => {
  const { data } = await api.get(`/admin/demo`);
  return data;
};
export const adminDemoRestore = async () => {
  const { data } = await api.post(`/admin/demo/restore`);
  return data;
};
export const adminDemoDelete = async () => {
  const { data } = await api.delete(`/admin/demo`);
  return data;
};
export const listCompanies = async () => {
  const { data } = await api.get(`/companies`);
  return data;
};

export const adminDemoLinks = async () => {
  const { data } = await api.get(`/admin/demo/links`);
  return data;
};
export const createDemoLink = async (payload) => {
  const { data } = await api.post(`/admin/demo/links`, payload);
  return data;
};
export const revokeDemoLink = async (id) => {
  const { data } = await api.delete(`/admin/demo/links/${id}`);
  return data;
};
export const pregenerateNarration = async () => {
  const { data } = await api.post(`/admin/demo/narration`, {}, { timeout: 180000 });
  return data;
};
export const getTourVoice = async () => (await api.get(`/public/tour-voice`)).data.mode;
export const setTourVoice = async (mode) => (await api.put(`/admin/demo/voice`, { mode })).data.mode;
export const getPublicDemo = async (token) => {
  const { data } = await api.get(`/public/demo/${token}`);
  return data;
};
export const tourAudioUrl = (key, step) => `${API_BASE}/public/tour-audio/${key}/${step}`;
export const publicFlowUrl = (token, fmt) => `${API_BASE}/public/demo/${token}/flow.${fmt}`;

// ----- Admin · Tenants -----
export const adminListTenants = async () => {
  const { data } = await api.get(`/admin/tenants`);
  return data;
};
export const adminUpsertTenant = async (slug, body) => {
  const { data } = await api.put(`/admin/tenants/${slug}`, body);
  return data;
};
export const adminDeleteTenant = async (slug) => {
  const { data } = await api.delete(`/admin/tenants/${slug}`);
  return data;
};

// ----- Admin · Users -----
export const adminListUsers = async (filters = {}) => {
  const { data } = await api.get(`/admin/users`, { params: filters });
  return data;
};
export const adminApproveUser = async (id) => {
  const { data } = await api.post(`/admin/users/${id}/approve`);
  return data;
};
export const adminRejectUser = async (id) => {
  const { data } = await api.post(`/admin/users/${id}/reject`);
  return data;
};
export const adminUpdateUserRole = async (id, role) => {
  const { data } = await api.put(`/admin/users/${id}/role`, { role });
  return data;
};
export const adminDeleteUser = async (id) => {
  const { data } = await api.delete(`/admin/users/${id}`);
  return data;
};

// ----- Per-agent KBL -----
export const agentKbl = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/kbl`);
  return data;
};

// ----- LLM credentials (BYO API keys) -----
export const llmProviders = async () => {
  const { data } = await api.get(`/admin/llm/providers`);
  return data;
};
export const llmGlobalKeys = async () => {
  const { data } = await api.get(`/admin/llm/keys`);
  return data;
};
export const llmGlobalSave = async (provider, { api_key, model }) => {
  const { data } = await api.put(`/admin/llm/keys/${provider}`, { api_key, model });
  return data;
};
export const llmGlobalDelete = async (provider) => {
  const { data } = await api.delete(`/admin/llm/keys/${provider}`);
  return data;
};
export const llmGlobalCheck = async (provider, { api_key, model } = {}) => {
  const { data } = await api.post(`/admin/llm/keys/${provider}/check`, { api_key, model });
  return data;
};
export const agentLlmGet = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/llm`);
  return data;
};
export const agentLlmSave = async (slug, { provider, api_key, model }) => {
  const { data } = await api.put(`/agents/${slug}/llm`, { provider, api_key, model });
  return data;
};
export const agentLlmDelete = async (slug) => {
  const { data } = await api.delete(`/agents/${slug}/llm`);
  return data;
};
export const agentLlmCheck = async (slug, { api_key, model } = {}) => {
  const { data } = await api.post(`/agents/${slug}/llm/check`, { api_key, model });
  return data;
};

// ---- AI Agents (one per department) ----
export const generateAgents = async (sid) => {
  const { data } = await api.post(`/sessions/${sid}/agents/generate`);
  return data;
};

export const listSessionAgents = async (sid) => {
  const { data } = await api.get(`/sessions/${sid}/agents`);
  return data;
};

export const getAgent = async (slug) => {
  const { data } = await api.get(`/agents/${slug}`);
  return data;
};

export const getAgentQa = async (slug) => (await api.get(`/agents/${slug}/qa`)).data;
export const generateAgentQa = async (slug) => (await api.post(`/agents/${slug}/qa/generate`, {}, { timeout: 120000 })).data;
export const saveAgentQa = async (slug, questions) => (await api.put(`/agents/${slug}/qa`, { questions })).data;
export const runAgentQa = async (slug) => (await api.post(`/agents/${slug}/qa/run`)).data;

export const updateAgent = async (slug, patch) => {
  const { data } = await api.put(`/agents/${slug}`, patch);
  return data;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const chatError = (detail) => Object.assign(new Error(detail), { response: { data: { detail } } });

// The answer is generated in the background and polled, so long replies never hit proxy timeouts.
export const startAgentMessage = async (slug, text, chatId, starterPromptId) => {
  const { data } = await api.post(`/agents/${slug}/chat`, {
    text,
    chat_id: chatId,
    starter_prompt_id: starterPromptId,
    background: true,
  });
  return data;
};

export const waitAgentReply = async (slug, chatId) => {
  const deadline = Date.now() + 6 * 60 * 1000;
  while (Date.now() < deadline) {
    await sleep(2500);
    let chat;
    try {
      chat = await getAgentChat(slug, chatId);
    } catch {
      continue;
    }
    if (chat.pending) continue;
    if (chat.error) throw chatError(chat.error);
    const last = chat.messages[chat.messages.length - 1];
    return last?.role === "agent" ? last : null;
  }
  throw chatError("El agente tardó demasiado en responder. Intente de nuevo.");
};

export const getAgentChat = async (slug, chatId) => {
  const { data } = await api.get(`/agents/${slug}/chats/${chatId}`);
  return data;
};

export const getToolsCatalogue = async () => {
  const { data } = await api.get(`/tools/catalogue`);
  return data;
};

// ---- Metrics & Dashboard ----
export const getDashboard = async (window = 1) => {
  const { data } = await api.get(`/dashboard`, { params: { window } });
  return data;
};

export const getAgentMetrics = async (slug, window = 1) => {
  const { data } = await api.get(`/agents/${slug}/metrics`, { params: { window } });
  return data;
};

export default api;
